import React, { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import { FaPaste, FaTimes } from "react-icons/fa";
import { getQuestion } from "../../api/mathBank";

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const parseQuestionIds = (input) => {
  const text = String(input || "").replace(
    /<math-question\b([^>]*)>(?:\s*<\/math-question\s*>)?/gi,
    (tag, attributes) => {
      const uuid = attributes.match(/(?:^|\s)uuid\s*=\s*(["'])(.*?)\1/i);
      return uuid ? `\n${uuid[2]}\n` : tag;
    },
  );
  const ids = [];
  const invalid = [];
  const seen = new Set();
  let duplicateCount = 0;
  text.split(/\r?\n|\r/).map((line) => line.trim()).filter(Boolean).forEach((line) => {
    if (!uuidPattern.test(line)) {
      invalid.push(line);
      return;
    }
    const id = line.toLowerCase();
    if (seen.has(id)) {
      duplicateCount += 1;
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return { ids, invalid, duplicateCount };
};

export default function BulkQuestionInsert({
  authToken, subject, existingIds, targetId, targetLabel, onInsert, disabled = false,
}) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const titleId = useId();
  const hintId = useId();
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const inFlightRef = useRef(false);
  // Use current selections when loading finishes, while keeping the original destination.
  const onInsertRef = useRef(onInsert);
  onInsertRef.current = onInsert;

  useEffect(() => {
    if (!isOpen) return undefined;
    const dialog = dialogRef.current;
    const trigger = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    inputRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      trigger?.focus();
    };
  }, [isOpen]);

  const close = () => {
    if (!inFlightRef.current) setIsOpen(false);
  };

  const parsedInput = parseQuestionIds(input);
  const existing = new Set(existingIds.map((id) => String(id).toLowerCase()));
  const pendingCount = parsedInput.ids.filter((id) => !existing.has(id)).length;
  const duplicateCount = parsedInput.duplicateCount + parsedInput.ids.length - pendingCount;

  const insert = async () => {
    if (inFlightRef.current || disabled) return;
    const parsed = parseQuestionIds(input);
    if (!parsed.ids.length) {
      setResult({ added: 0, skipped: parsed.duplicateCount, invalid: parsed.invalid, failed: [],
        error: "請貼上有效的題目 UUID 或 <math-question> 標籤。" });
      return;
    }
    const existing = new Set(existingIds.map((id) => String(id).toLowerCase()));
    const ids = parsed.ids.filter((id) => !existing.has(id));
    const nextResult = {
      added: 0,
      skipped: parsed.duplicateCount + parsed.ids.length - ids.length,
      invalid: parsed.invalid,
      failed: [],
    };
    inFlightRef.current = true;
    setLoading(true);
    setResult(null);
    try {
      const questions = [];
      // Bound parallel requests and retain pasted order even if requests finish out of order.
      for (let offset = 0; offset < ids.length; offset += 5) {
        const batch = ids.slice(offset, offset + 5);
        const responses = await Promise.allSettled(batch.map((id) => getQuestion({ authToken, subject, id })));
        responses.forEach((response, index) => {
          if (response.status === "fulfilled" && response.value?.id) {
            questions.push(response.value);
          } else {
            nextResult.failed.push({
              id: batch[index],
              message: response.reason?.status === 404
                ? "找不到題目，請確認 ID 與目前科目"
                : response.reason?.message || "題目讀取失敗",
            });
          }
        });
      }
      if (questions.length) {
        nextResult.added = onInsertRef.current(questions, targetId);
        nextResult.skipped += questions.length - nextResult.added;
      }
      setResult(nextResult);
    } catch (error) {
      setResult({ ...nextResult, error: error.message || "插入失敗，請再試一次。" });
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  const hasErrors = result && (result.error || result.invalid.length || result.failed.length);
  return (
    <>
      <LaunchRow>
        <ActionButton ref={triggerRef} type="button" aria-haspopup="dialog"
          disabled={disabled} onClick={() => setIsOpen(true)}>
          <FaPaste aria-hidden="true" />多個 ID 插入
        </ActionButton>
        <small>{disabled ? "請先選取題目區塊或考卷部分" : "貼上 ID 或題目標籤，批次加入"}</small>
      </LaunchRow>
      {isOpen && <InsertDialog ref={dialogRef} aria-modal="true" tabIndex={-1}
        aria-labelledby={titleId} aria-describedby={hintId}
        onCancel={(event) => { event.preventDefault(); close(); }}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const controls = event.currentTarget.querySelectorAll("button:not(:disabled), textarea:not(:disabled)");
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (!first) {
            event.preventDefault();
            event.currentTarget.focus();
          } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right ||
              event.clientY < bounds.top || event.clientY > bounds.bottom) close();
        }}>
        <DialogHeader>
          <div>
            <h2 id={titleId}>多個 ID 插入</h2>
            <p id={hintId}>貼上題目 ID 或標籤，依照貼上順序加入。</p>
          </div>
          <CloseButton type="button" aria-label="關閉多個 ID 插入" disabled={loading} onClick={close}>
            <FaTimes aria-hidden="true" />
          </CloseButton>
        </DialogHeader>
        <InsertBody aria-busy={loading}>
          <Destination>
            <small>加入位置</small>
            <strong>{disabled ? "請先選取題目區塊或考卷部分" : targetLabel}</strong>
          </Destination>
          <InputLabel>
            <span>題目 ID 或標籤</span>
            <ActionButton type="button" disabled={loading || !input} onClick={() => {
              setInput(""); setResult(null); inputRef.current?.focus();
            }}>清空</ActionButton>
          </InputLabel>
          <textarea
            ref={inputRef}
            aria-label="多個題目 ID 或 math-question 標籤"
            rows={7}
            placeholder={'每行一個題目 ID\n例如：218d620e-fb58-47c0-84cf-94bc74bdda69\n\n也可以直接貼上多個 <math-question uuid="…"></math-question> 標籤'}
            value={input}
            disabled={loading}
            spellCheck={false}
            onChange={(event) => { setInput(event.target.value); setResult(null); }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (input.trim()) insert();
              }
            }}
          />
          <InputSummary aria-live="polite" aria-atomic="true">
            <div><strong>{pendingCount}</strong><span>可插入題目</span></div>
            <div><strong>{duplicateCount}</strong><span>重複 ID · 略過</span></div>
            <div data-invalid={parsedInput.invalid.length > 0}><strong>{parsedInput.invalid.length}</strong><span>格式不正確</span></div>
          </InputSummary>
          <p>重複 ID 與格式不正確的內容會自動略過。支援 ⌘Enter / Ctrl+Enter 插入。</p>
          {result && (
            <InsertResult role={hasErrors ? "alert" : "status"} $error={Boolean(hasErrors)}>
              <div>已插入 {result.added} 題{result.skipped ? `，跳過 ${result.skipped} 個重複 ID` : ""}。</div>
              {result.error && <div>{result.error}</div>}
              {result.invalid.length > 0 && <>
                <div>以下 {result.invalid.length} 筆格式不正確：</div>
                <ul>{result.invalid.map((value, index) => <li key={index}>{value}</li>)}</ul>
              </>}
              {result.failed.length > 0 && <>
                <div>以下 {result.failed.length} 題讀取失敗，可保留內容再試一次：</div>
                <ul>{result.failed.map(({ id, message }) => <li key={id}>{id}：{message}</li>)}</ul>
              </>}
            </InsertResult>
          )}
        </InsertBody>
        <DialogFooter>
          <ActionButton type="button" onClick={close} disabled={loading}>{result ? "完成" : "取消"}</ActionButton>
          <ActionButton $primary type="button" onClick={insert} disabled={disabled || loading || !input.trim()}>
            <FaPaste aria-hidden="true" />{loading ? "插入中…" : "插入題目"}
          </ActionButton>
        </DialogFooter>
      </InsertDialog>}
    </>
  );
}

const LaunchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-width: 0;
  margin: 12px 0;
  small { font-size: 12px; color: var(--app-muted-text, #5f6368); }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 8px;
  padding: 8px 14px;
  background: ${({ $primary }) => $primary ? "var(--app-nav-active-bg, #202124)" : "var(--app-surface, #fff)"};
  color: ${({ $primary }) => $primary ? "var(--app-nav-active-text, #fff)" : "var(--app-surface-text, #202124)"};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:not(:disabled):hover { filter: brightness(0.94); }
  &:focus-visible { outline: 2px solid var(--app-icon-accent, #202124); outline-offset: 3px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const InsertDialog = styled.dialog`
  width: min(600px, calc(100% - 32px));
  max-height: calc(100dvh - 32px);
  box-sizing: border-box;
  padding: 0;
  overflow: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 14px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.3);
  &::backdrop { background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72)); }
`;

const DialogHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px;
  border-bottom: 1px solid var(--app-border, #d5d7db);
  h2 { margin: 0 0 6px; font-size: 20px; font-weight: 700; }
  p { margin: 0; color: var(--app-muted-text, #5f6368); font-size: 13px; }
`;

const CloseButton = styled(ActionButton)`
  flex-shrink: 0;
  padding: 8px;
  width: 38px;
  border-color: transparent;
`;

const Destination = styled.div`
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--app-surface-soft, #f4f5f7);
  overflow-wrap: anywhere;
  small { color: var(--app-muted-text, #5f6368); font-size: 12px; }
  strong { font-size: 14px; }
`;

const InputLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  button { min-height: 30px; padding: 4px 10px; font-size: 12px; }
`;

const InsertBody = styled.div`
  display: grid;
  gap: 12px;
  padding: 20px;
  textarea {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    resize: vertical;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 8px;
    padding: 12px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    font: inherit;
    font-size: 13px;
    line-height: 1.6;
    &:focus { outline: 2px solid var(--app-icon-accent, #202124); outline-offset: 2px; }
    &::placeholder { color: var(--app-muted-text, #5f6368); opacity: 1; }
  }
  p { margin: 0; font-size: 13px; color: var(--app-muted-text, #5f6368); }
`;

const InputSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  > div { display: grid; gap: 3px; padding: 10px; border: 1px solid var(--app-border, #d5d7db); border-radius: 8px; }
  strong { font-size: 22px; line-height: 1.2; }
  span { color: var(--app-muted-text, #5f6368); font-size: 12px; }
  [data-invalid="true"] { color: var(--app-danger-text, #b3261e); background: var(--app-danger-bg, #fff4f4); }
`;

const DialogFooter = styled.footer`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border, #d5d7db);
  background: var(--app-surface, #fff);
`;

const InsertResult = styled.div`
  font-size: 13px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ $error }) => $error ? "var(--app-danger-bg, #fff4f4)" : "var(--app-surface-soft, #f4f5f7)"};
  overflow-wrap: anywhere;
  color: ${({ $error }) => $error ? "var(--app-danger-text, #b3261e)" : "var(--app-input-text, #202124)"};
  ul { margin: 4px 0; padding-left: 20px; }
`;
